import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound, Phone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Button, Card, Checkbox, Input, OtpInput, useToast } from '@/components/ui';
import { loginSchema, LoginFormValues } from '@/features/auth/loginSchema';
import useAuth from '@/hooks/useAuth';
import { errorMessage } from '@/utils/apiError';
import { homeRouteFor, ROUTES, STORAGE_KEYS } from '@/utils/constants';

// Only the fallback for a reply that carries no retryAfterSeconds; the server
// sends the real remaining wait on every response, so the button unlocks at the
// same moment the server starts accepting again.
const RESEND_SECONDS = 30;

// A resend gap is seconds; a lockout is a quarter of an hour. Counting down from
// "900s" reads as a broken timer rather than as a wait with an end.
const formatWait = (seconds: number): string =>
  seconds >= 90 ? `${Math.ceil(seconds / 60)}m` : `${seconds}s`;

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { sendOtp, isSendingOtp, verifyOtp, isVerifyingOtp } = useAuth();

  // apiClient sets this flag right before a hard-redirect here on forced logout
  // (expired/revoked session), since a full page reload can't carry React state.
  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEYS.SESSION_EXPIRED)) {
      sessionStorage.removeItem(STORAGE_KEYS.SESSION_EXPIRED);
      toast.info('Your session has expired. Please sign in again.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<LoginFormValues>({ resolver: zodResolver(loginSchema) });

  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [remember, setRemember] = useState(true);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (!resendIn) return undefined;
    const timer = setTimeout(() => setResendIn((n) => Math.max(0, n - 1)), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const requestOtp = async (values: LoginFormValues) => {
    try {
      const result = await sendOtp(values.mobile);
      // The server's own remaining wait, not a guess. It used to answer "OTP
      // sent" to a request it had silently dropped inside the resend window,
      // which is exactly the case where no SMS ever arrived.
      setResendIn(result.retryAfterSeconds || RESEND_SECONDS);

      // A lockout is the one case that does NOT advance to the code step: there
      // is no code to enter, and showing the input invites the user to keep
      // guessing into a wall.
      if (result.locked) {
        setOtpSent(false);
        toast.error(
          `Too many incorrect attempts. Try again in ${Math.ceil(result.retryAfterSeconds / 60)} minute(s).`,
        );
        return;
      }

      setOtpSent(true);
      setOtpError('');
      if (!result.otpSent) {
        // Nothing new was sent, so the earlier code is still the live one -
        // clearing the input here would wipe a half-typed valid code.
        toast.info(
          `A code was already sent to +91 ${values.mobile}. Check your messages, ` +
            `or resend in ${result.retryAfterSeconds}s.`,
        );
        return;
      }

      // A fresh code invalidates whatever was typed against the previous one.
      setOtp('');
      // Show the dev OTP (TERMINAL mode only) so it can be read without the
      // server console, but never auto-fill the input - the user types it in.
      toast.info(result.devOtp ? `Dev OTP: ${result.devOtp}` : `OTP sent to +91 ${values.mobile}`);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not send OTP. Please try again.'));
    }
  };

  const submitOtp = async (value: string = otp) => {
    if (value.length !== 6) {
      setOtpError('Enter the 6-digit OTP');
      return;
    }
    setOtpError('');
    try {
      const session = await verifyOtp({
        mobile: getValues('mobile'),
        otp: value,
        rememberMe: remember,
      });
      toast.success('Signed in');
      const from = (location.state as { from?: { pathname: string } } | null)?.from;
      void navigate(from?.pathname ?? homeRouteFor(session.user), { replace: true });
    } catch (error) {
      setOtpError(errorMessage(error, 'Incorrect or expired OTP'));
    }
  };

  const changeNumber = () => {
    setOtpSent(false);
    setOtp('');
    setOtpError('');
  };

  return (
    <div className="w-full max-w-sm">
      <Card padding="lg">
        <h1 className="text-2xl font-bold text-ink">Login</h1>
        <p className="mt-1.5 text-sm text-ink/60">
          Enter your registered mobile number to receive a one-time code.
        </p>

        <form
          onSubmit={(e) => {
            handleSubmit(requestOtp)(e).catch(() => {});
          }}
          noValidate
          className="mt-6 flex flex-col gap-4"
        >
          <div className="flex flex-col gap-1">
            {/* The Input below renders id="mobile", so htmlFor is a real
                association; this rule's config here rejects the htmlFor-only form. */}
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="mobile" className="text-sm font-medium text-ink">
              Mobile number
            </label>
            <div className="flex items-start gap-2">
              <span className="flex h-11 shrink-0 items-center rounded-md border border-line bg-cream px-3 text-sm font-semibold text-ink/60 sm:h-9">
                +91
              </span>
              <div className="min-w-0 flex-1">
                <Input
                  id="mobile"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  maxLength={10}
                  placeholder="10-digit mobile"
                  error={errors.mobile?.message}
                  disabled={otpSent}
                  className="w-full"
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...register('mobile')}
                />
              </div>
            </div>
          </div>

          {otpSent && (
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-ink">Enter OTP</span>
                <button
                  type="button"
                  disabled={resendIn > 0 || isSendingOtp}
                  onClick={() => {
                    handleSubmit(requestOtp)().catch(() => {});
                  }}
                  className="-my-2 -mr-2 inline-flex min-h-11 items-center rounded px-2 py-2 text-xs font-semibold text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:text-ink/40 disabled:no-underline"
                >
                  {resendIn > 0 ? `Resend in ${formatWait(resendIn)}` : 'Resend OTP'}
                </button>
              </div>
              <OtpInput
                value={otp}
                onChange={setOtp}
                onComplete={(value) => {
                  void submitOtp(value);
                }}
                error={otpError}
                disabled={isVerifyingOtp}
              />
              <p className="text-xs text-ink/60">
                Code sent to +91 {getValues('mobile')}.{' '}
                <button
                  type="button"
                  onClick={changeNumber}
                  className="-my-2 inline-block min-h-11 rounded px-1 py-2 align-middle font-semibold text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                >
                  Change number
                </button>
              </p>
            </div>
          )}

          <Checkbox
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            label="Remember me on this device"
          />

          {otpSent ? (
            <Button
              type="button"
              onClick={() => {
                void submitOtp();
              }}
              disabled={isVerifyingOtp}
              className="min-h-11 w-full gap-1.5 sm:min-h-0"
            >
              <KeyRound className="h-4 w-4" /> Verify &amp; login
            </Button>
          ) : (
            <Button
              type="submit"
              disabled={isSendingOtp}
              className="min-h-11 w-full gap-1.5 sm:min-h-0"
            >
              <Phone className="h-4 w-4" /> Send OTP
            </Button>
          )}
        </form>

        {/* `inline-flex` inside a flex row, not `inline-block` + `align-middle`:
            the link carries a 44px touch target, and as an inline-block that box
            is aligned by its own vertical-align against the text baseline, which
            dropped the label visibly below the sentence it belongs to. A flex
            line box centres the two against each other instead. */}
        <p className="-mb-2 mt-4 flex flex-wrap items-center justify-center gap-x-1 text-xs text-ink/60">
          Don&apos;t have an account?
          <Link
            to={ROUTES.REGISTER}
            className="-my-2 inline-flex min-h-11 items-center rounded px-1 font-semibold text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            Request Access
          </Link>
        </p>
      </Card>

      <p className="mt-3 text-center text-xs text-ink/60">
        Protected by role-based access and OTP.
      </p>
    </div>
  );
};

export default LoginPage;
